'use client';

/**
 * ImportProgress Component
 *
 * Displays import progress with real-time updates and animated progress bar.
 * Shows current status, processed count, and estimated time remaining.
 */

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useEffect, useState } from 'react';
import { getNeonStyles, type NeonAccent, neonAccents } from '../../theme/palette';
import { createNeonGlow, glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';
import type { ImportResult } from '../../types/entityFramework';

// ============================================================================
// Types
// ============================================================================

export interface ImportProgressProps {
  /**
   * Current progress (0-100)
   */
  progress: number;

  /**
   * Current status message
   */
  status: string;

  /**
   * Total number of rows being processed
   */
  totalRows: number;

  /**
   * Number of rows processed so far
   */
  processedRows: number;

  /**
   * Whether the import is complete
   */
  isComplete: boolean;

  /**
   * Import result (available when complete)
   */
  result?: ImportResult;

  /**
   * Neon accent color
   * @default "cyan"
   */
  neonAccent?: NeonAccent;

  /**
   * Additional class name
   */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    padding: 16,
    borderRadius: 16,
    backgroundColor: glass.background,
    border: `1px solid ${glass.border}`,
    backdropFilter: glass.backdropBlur,
  };
});

const StyledProgressWrapper = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $neonAccent: NeonAccent;
  $isDark: boolean;
}>(({ $neonAccent, $isDark }) => {
  const neonStyles = getNeonStyles($neonAccent, $isDark);

  return {
    position: 'relative',
    marginBottom: 24,

    '& .MuiLinearProgress-root': {
      height: 12,
      borderRadius: 6,
      backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
      overflow: 'hidden',
    },

    '& .MuiLinearProgress-bar': {
      borderRadius: 6,
      background: `linear-gradient(90deg, ${neonStyles.base} 0%, ${alpha(neonStyles.base, 0.7)} 100%)`,
      boxShadow: createNeonGlow($neonAccent, 'subtle'),
      transition: 'transform 0.3s ease-out',
    },
  };
});

const StyledStatusIcon = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $status: 'processing' | 'complete' | 'error';
  $neonAccent: NeonAccent;
}>(({ theme, $status, $neonAccent }) => {
  const getColor = () => {
    switch ($status) {
      case 'complete':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      default:
        return neonAccents[$neonAccent].base;
    }
  };

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 80,
    height: 80,
    borderRadius: '50%',
    backgroundColor: alpha(getColor(), 0.1),
    color: getColor(),
    marginBottom: 16,
  };
});

const StyledStatsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: 16,
  marginTop: 24,
});

const StyledStatItem = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $color: string;
}>(({ $isDark, $color }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: 16,
  borderRadius: 12,
  backgroundColor: alpha($color, $isDark ? 0.1 : 0.06),
  border: `1px solid ${alpha($color, 0.2)}`,
}));

const StyledDetailRow = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 0',
  borderBottom: `1px solid ${$isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,

  '&:last-child': {
    borderBottom: 'none',
  },
}));

// ============================================================================
// Component
// ============================================================================

export const ImportProgress: React.FC<ImportProgressProps> = ({
  progress,
  status,
  totalRows,
  processedRows,
  isComplete,
  result,
  neonAccent = 'cyan',
  className,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const neonStyles = getNeonStyles(neonAccent, isDark);

  const [displayedProgress, setDisplayedProgress] = useState(0);
  const [startTime] = useState(Date.now());
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<string | null>(null);

  // Animate progress smoothly
  useEffect(() => {
    const timer = setTimeout(() => {
      setDisplayedProgress(progress);
    }, 50);
    return () => clearTimeout(timer);
  }, [progress]);

  // Calculate estimated time remaining
  useEffect(() => {
    if (processedRows > 0 && !isComplete) {
      const elapsedMs = Date.now() - startTime;
      const msPerRow = elapsedMs / processedRows;
      const remainingRows = totalRows - processedRows;
      const remainingMs = msPerRow * remainingRows;

      if (remainingMs > 0) {
        const seconds = Math.ceil(remainingMs / 1000);
        if (seconds < 60) {
          setEstimatedTimeRemaining(`${seconds}s remaining`);
        } else {
          const minutes = Math.ceil(seconds / 60);
          setEstimatedTimeRemaining(`~${minutes}m remaining`);
        }
      }
    } else if (isComplete) {
      setEstimatedTimeRemaining(null);
    }
  }, [processedRows, totalRows, isComplete, startTime]);

  const getStatusType = (): 'processing' | 'complete' | 'error' => {
    if (result?.success === false) return 'error';
    if (isComplete) return 'complete';
    return 'processing';
  };

  const statusType = getStatusType();

  return (
    <StyledContainer $isDark={isDark} className={className}>
      {/* Progress Section */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <AnimatePresence mode="wait">
          <StyledStatusIcon
            key={statusType}
            $status={statusType}
            $neonAccent={neonAccent}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {statusType === 'processing' ? (
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                <SyncIcon sx={{ fontSize: iconSizes['2xl'] }} />
              </motion.div>
            ) : statusType === 'complete' ? (
              <CheckCircleIcon sx={{ fontSize: iconSizes['2xl'] }} />
            ) : (
              <ErrorIcon sx={{ fontSize: iconSizes['2xl'] }} />
            )}
          </StyledStatusIcon>
        </AnimatePresence>

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          {isComplete ? (result?.success ? 'Import Complete!' : 'Import Failed') : 'Importing Data...'}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {status}
        </Typography>
      </Box>
      {/* Progress Bar */}
      <StyledProgressWrapper $neonAccent={neonAccent} $isDark={isDark}>
        <LinearProgress
          variant="determinate"
          value={displayedProgress}
          sx={{
            '& .MuiLinearProgress-bar': {
              transition: transitionPresets.all,
            },
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {processedRows.toLocaleString()} / {totalRows.toLocaleString()} rows
          </Typography>

          <Typography variant="caption" sx={{ color: neonStyles.base, fontWeight: 600 }}>
            {Math.round(displayedProgress)}%
          </Typography>
        </Box>

        {estimatedTimeRemaining && !isComplete && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                textAlign: 'center',
                mt: 1,
              }}
            >
              {estimatedTimeRemaining}
            </Typography>
          </motion.div>
        )}
      </StyledProgressWrapper>
      {/* Result Statistics (when complete) */}
      {isComplete && result && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <StyledStatsGrid>
            <StyledStatItem $isDark={isDark} $color={theme.palette.info.main}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                {result.totalProcessed.toLocaleString()}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Processed
              </Typography>
            </StyledStatItem>

            <StyledStatItem $isDark={isDark} $color={theme.palette.success.main}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                {result.created.toLocaleString()}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Created
              </Typography>
            </StyledStatItem>

            <StyledStatItem $isDark={isDark} $color={theme.palette.warning.main}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                {result.updated.toLocaleString()}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Updated
              </Typography>
            </StyledStatItem>

            <StyledStatItem $isDark={isDark} $color={theme.palette.error.main}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.error.main }}>
                {result.failed.toLocaleString()}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Failed
              </Typography>
            </StyledStatItem>
          </StyledStatsGrid>

          {/* Detailed breakdown */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
              Import Summary
            </Typography>

            <StyledDetailRow $isDark={isDark}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Total Processed
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {result.totalProcessed.toLocaleString()} records
              </Typography>
            </StyledDetailRow>

            <StyledDetailRow $isDark={isDark}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Successfully Created
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                {result.created.toLocaleString()} records
              </Typography>
            </StyledDetailRow>

            <StyledDetailRow $isDark={isDark}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Updated (Existing)
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.warning.main }}>
                {result.updated.toLocaleString()} records
              </Typography>
            </StyledDetailRow>

            <StyledDetailRow $isDark={isDark}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Skipped (Duplicates)
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {result.skipped.toLocaleString()} records
              </Typography>
            </StyledDetailRow>

            {result.failed > 0 && (
              <StyledDetailRow $isDark={isDark}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Failed
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.error.main }}>
                  {result.failed.toLocaleString()} records
                </Typography>
              </StyledDetailRow>
            )}
          </Box>

          {/* Error details if any */}
          {result.errors.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: theme.palette.error.main,
                }}
              >
                Failed Records ({result.errors.length})
              </Typography>

              <Box
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  borderRadius: 2,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                }}
              >
                {result.errors.slice(0, 10).map((error, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1.5,
                      borderBottom:
                        index < result.errors.length - 1
                          ? `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
                          : 'none',
                    }}
                  >
                    <Typography variant="body2" color="error">
                      Row {error.row}: {error.message}
                    </Typography>
                  </Box>
                ))}

                {result.errors.length > 10 && (
                  <Box sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      ... and {result.errors.length - 10} more errors
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </motion.div>
      )}
    </StyledContainer>
  );
};

ImportProgress.displayName = 'ImportProgress';

export default ImportProgress;
